import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";

const PROD_DOMAIN = "https://www.thesecretseller.co.uk";

export default function LandingNewBuildPage() {
  return (
    <main data-testid="landing-new-build" className="pt-24">
      <Helmet>
        <title>Property Marketing for New Build Developments | 3D Renders & Virtual Staging | The Secret Seller</title>
        <meta name="description" content="3D renders and virtual staging for UK property developers. Market off-plan developments with photorealistic CGI exteriors and virtually staged interiors." />
        <link rel="canonical" href={`${PROD_DOMAIN}/property-marketing-new-builds`} />
      </Helmet>

      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-5xl mx-auto px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">For Developers</p>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-[2.8rem] font-extralight text-[#0D2D54] leading-[1.1] tracking-[-0.01em] mb-6">
                Property marketing for new build developments
              </h1>
              <p className="text-[15px] text-[#6B7280] leading-[1.8] mb-8">
                Sell off-plan properties before a single brick is laid. Our photorealistic 3D renders and virtual staging help developers create compelling marketing materials that drive reservations and sales.
              </p>
              <Link to="/3d-property-rendering-uk" className="inline-flex items-center gap-3 bg-[#0D2D54] text-white px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all">
                Request a Quote <ArrowRight size={14} />
              </Link>
            </div>
            <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/mlpg0mca_stockton-roadexteriorfinal.jpg" alt="3D exterior render of new build development" className="w-full h-auto" />
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-8 lg:px-16">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-[#0D2D54] mb-10 text-center">What we offer developers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mb-3">3D Exterior Renders</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed mb-4">Photorealistic CGI visualisations of your development exterior — perfect for planning applications, sales brochures and website marketing.</p>
              <ul className="space-y-2 text-sm text-[#6B7280]">
                {["Daytime and twilight scenes", "Aerial and street-level perspectives", "Accurate material and lighting"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2"><Check size={14} className="text-[#758C7B]" />{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mb-3">Interior Virtual Staging</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed mb-4">Digitally furnish show home interiors before construction is complete. Give buyers a clear vision of each room's potential.</p>
              <ul className="space-y-2 text-sm text-[#6B7280]">
                {["Multiple room layouts and styles", "Kitchen, bedroom, living and bathroom staging", "Consistent design language across a development"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2"><Check size={14} className="text-[#758C7B]" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#FAFAF8]">
        <div className="max-w-5xl mx-auto px-8 lg:px-16">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-[#0D2D54] mb-10 text-center">Our work</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/mlpg0mca_stockton-roadexteriorfinal.jpg" alt="3D exterior render" className="w-full h-auto" />
            <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/e5e00aek_stocktonroadkitchenaltfinal.jpg" alt="Virtual staging - kitchen render" className="w-full h-auto" />
            <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/v0qjqdwv_stocktonroadofficefinal.jpg" alt="Virtual staging - office render" className="w-full h-auto" />
            <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/3s5sc4rd_stocktonroadbedroom2final.jpg" alt="Virtual staging - bedroom render" className="w-full h-auto" />
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#0D2D54]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-white mb-6">Marketing your next development?</h2>
          <p className="text-[15px] text-white/50 mb-10">Tell us about your project and we'll provide a tailored quote within 24 hours.</p>
          <Link to="/3d-property-rendering-uk" className="inline-flex items-center gap-3 bg-white text-[#0D2D54] px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-white/90 transition-all">
            Request a Quote <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
